/* eslint-disable no-console */
import {useCallback, useEffect, useRef} from 'react';
import {
  AccessibilityInfo,
  Alert,
  AppState,
  AppStateStatus,
  Linking,
  Platform,
} from 'react-native';
import {check, PERMISSIONS, request, RESULTS} from 'react-native-permissions';

const ImagePicker =
  Platform.OS === 'web'
    ? null
    : require('react-native-image-crop-picker').default;

const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024;
const COMPRESS_IMAGE_QUALITY = 0.8;

const PNG_MIME_TYPES = ['image/png', 'image/x-png'];
const JPEG_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/pjpeg',
  'image/x-jpeg',
];

export const IMAGE_SPECS = {
  avatar: {
    width: 400,
    height: 400,
    label: 'Update Profile Photo',
    hint: 'Max 2MB. Recommended: PNG or JPG',
  },
  transformation: {
    width: 1200,
    height: 1600,
    label: 'Update Transformation Photo',
    hint: 'Max 2MB. Recommended: PNG or JPG',
  },
} as const;

export type PickerType = keyof typeof IMAGE_SPECS;
type PickerSource = 'camera' | 'library';

export type PickedImage = {
  uri: string;
  mimeType: string;
  base64: string;
  cacheKey?: string;
};

type PendingRequest = {
  type: PickerType;
  source: PickerSource;
  onSuccess: (image: PickedImage) => void;
} | null;

const normalizeMime = (mime?: string) => (mime || '').toLowerCase().trim();

const isPngMime = (mime?: string) => {
  const normalizedMime = normalizeMime(mime);

  return (
    PNG_MIME_TYPES.includes(normalizedMime) || normalizedMime.includes('png')
  );
};

const isJpegMime = (mime?: string) => {
  const normalizedMime = normalizeMime(mime);

  return (
    JPEG_MIME_TYPES.includes(normalizedMime) ||
    normalizedMime.includes('jpeg') ||
    normalizedMime.includes('jpg')
  );
};

const announce = (message: string) => {
  AccessibilityInfo.announceForAccessibility?.(message);
};

const showWebAlert = (message: string) => {
  const browserWindow = globalThis as any;

  if (browserWindow?.alert) {
    browserWindow.alert(message);
  }
};

const resizeAndCompressImageWeb = (
  file: File,
  targetWidth: number,
  targetHeight: number,
  quality: number = COMPRESS_IMAGE_QUALITY,
): Promise<{dataUrl: string; base64: string; mimeType: string}> =>
  new Promise((resolve, reject) => {
    const browserWindow = globalThis as any;
    const browserDocument = browserWindow?.document;

    if (!browserDocument || !browserWindow?.Image) {
      reject(new Error('Browser image APIs are not available'));
      return;
    }

    const img = new browserWindow.Image();
    const objectUrl = browserWindow.URL.createObjectURL(file);

    img.onload = () => {
      browserWindow.URL.revokeObjectURL(objectUrl);

      const canvas = browserDocument.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

      const outputMimeType = 'image/jpeg';

      const dataUrl = canvas.toDataURL(outputMimeType, quality);

      const base64 = dataUrl.split(',')[1] || '';

      resolve({
        dataUrl,
        base64,
        mimeType: outputMimeType,
      });
    };

    img.onerror = () => {
      browserWindow.URL.revokeObjectURL(objectUrl);
      reject(new Error('Failed to load image'));
    };

    img.src = objectUrl;
  });

export const useImagePicker = () => {
  const appState = useRef<AppStateStatus>(AppState.currentState);
  const pendingRequest = useRef<PendingRequest>(null);

  const getPermissionType = (source: PickerSource) => {
    if (Platform.OS === 'ios') {
      return source === 'camera'
        ? PERMISSIONS.IOS.CAMERA
        : PERMISSIONS.IOS.PHOTO_LIBRARY;
    }

    if (Platform.OS === 'android') {
      if (source === 'camera') {
        return PERMISSIONS.ANDROID.CAMERA;
      }

      if (Platform.Version >= 33) {
        return undefined;
      }

      return PERMISSIONS.ANDROID.READ_EXTERNAL_STORAGE;
    }

    return undefined;
  };

  const showSettingsAlert = useCallback(
    (
      source: PickerSource,
      type: PickerType,
      onSuccess: (image: PickedImage) => void,
    ) => {
      pendingRequest.current = {source, type, onSuccess};

      const title = `${
        source === 'camera' ? 'Camera' : 'Photo Library'
      } Access Disabled`;
      const message = 'Please enable access in Settings to upload photos.';

      Alert.alert(title, message, [
        {
          text: 'Cancel',
          style: 'cancel',
          onPress: () => {
            pendingRequest.current = null;
          },
        },
        {
          text: 'Open Settings',
          onPress: () => {
            Linking.openSettings();
          },
        },
      ]);

      announce(`${title}. ${message}`);
    },
    [],
  );

  const launchWebPicker = useCallback(
    async (type: PickerType, onSuccess: (image: PickedImage) => void) => {
      const browserWindow = globalThis as any;
      const browserDocument = browserWindow?.document;

      if (!browserDocument) {
        console.error('[launchWebPicker] No document available');
        showWebAlert('Image upload is currently not supported.');
        return;
      }

      const input = browserDocument.createElement('input');
      input.type = 'file';
      input.accept = 'image/png,image/jpeg,image/jpg';
      input.style.display = 'none';

      let cleanedUp = false;

      const cleanup = () => {
        if (!cleanedUp && input.parentNode) {
          input.parentNode.removeChild(input);
          cleanedUp = true;
        }
      };

      input.onchange = async (event: any) => {
        try {
          const file = event.target.files?.[0];
          event.target.value = '';

          if (!file) {
            cleanup();
            return;
          }

          const spec = IMAGE_SPECS[type];

          const processedImage = await resizeAndCompressImageWeb(
            file,
            spec.width,
            spec.height,
          );

          if (processedImage.base64.length > MAX_FILE_SIZE_BYTES * 1.4) {
            showWebAlert(
              'Image Too Large. This image exceeds the 2MB limit. Please choose a smaller photo.',
            );
            cleanup();
            return;
          }

          const result: PickedImage = {
            uri: processedImage.dataUrl,
            mimeType: processedImage.mimeType,
            base64: processedImage.base64,
            cacheKey: `${type}-${Date.now()}`,
          };

          onSuccess(result);
        } catch (error) {
          console.error('[launchWebPicker] Error processing image:', error);
          showWebAlert('Error processing image. Please try again.');
        } finally {
          setTimeout(cleanup, 100);
        }
      };

      input.oncancel = () => {
        cleanup();
      };

      browserDocument.body.appendChild(input);
      input.click();
    },
    [],
  );

  const launch = useCallback(
    async (
      source: PickerSource,
      type: PickerType,
      onSuccess: (image: PickedImage) => void,
    ) => {
      if (Platform.OS === 'web') {
        launchWebPicker(type, onSuccess);
        return;
      }

      try {
        const spec = IMAGE_SPECS[type];

        const options = {
          cropping: true,
          includeBase64: true,
          forceJpg: false,
          freeStyleCropEnabled: false,
          mediaType: 'photo' as const,
          width: spec.width,
          height: spec.height,
          compressImageQuality: COMPRESS_IMAGE_QUALITY,
        };

        const image =
          source === 'camera'
            ? await ImagePicker.openCamera(options)
            : await ImagePicker.openPicker(options);

        let finalImage = image;

        if (!isPngMime(image.mime) && !isJpegMime(image.mime)) {
          finalImage = await ImagePicker.openCropper({
            path: image.path,
            cropping: true,
            includeBase64: true,
            forceJpg: true,
            freeStyleCropEnabled: false,
            mediaType: 'photo' as const,
            width: spec.width,
            height: spec.height,
            compressImageQuality: COMPRESS_IMAGE_QUALITY,
          });
        }

        if (finalImage.size > MAX_FILE_SIZE_BYTES) {
          const title = 'Image Too Large';
          const message =
            'This image exceeds the 2MB limit. Please choose a smaller photo.';

          Alert.alert(title, message, [{text: 'OK'}]);
          announce(`${title}. ${message}`);
          return;
        }

        if (!finalImage.data) {
          const title = 'Error';
          const message = 'Could not prepare image. Please try again.';

          Alert.alert(title, message);
          announce(`${title}. ${message}`);
          return;
        }

        pendingRequest.current = null;

        const result: PickedImage = {
          uri: finalImage.path,
          mimeType: finalImage.mime,
          base64: finalImage.data,
          cacheKey: `${type}-${Date.now()}`,
        };

        onSuccess(result);
      } catch (e: any) {
        if (
          e?.code === 'E_PICKER_CANCELLED' ||
          e?.message?.includes('cancelled') ||
          e?.message?.includes('User cancelled')
        ) {
          return;
        }

        const message = String(e?.message || '').toLowerCase();
        const code = String(e?.code || '').toLowerCase();

        const isPermissionError =
          code.includes('permission') ||
          code.includes('denied') ||
          code.includes('blocked') ||
          message.includes('permission') ||
          message.includes('denied') ||
          message.includes('blocked') ||
          message.includes('photo library') ||
          message.includes('camera') ||
          message.includes('cannot open') ||
          message.includes('cannot access') ||
          message.includes('required permission missing');

        if (isPermissionError) {
          showSettingsAlert(source, type, onSuccess);
          return;
        }

        const title = 'Error';
        const errorMessage = 'Could not open picker. Please try again.';

        console.error('[APP PICKER ERROR]', e);

        Alert.alert(title, errorMessage);
        announce(`${title}. ${errorMessage}`);
      }
    },
    [launchWebPicker, showSettingsAlert],
  );

  const preLaunchCheck = useCallback(
    async (
      source: PickerSource,
      type: PickerType,
      onSuccess: (image: PickedImage) => void,
    ) => {
      const permission = getPermissionType(source);

      if (!permission) {
        launch(source, type, onSuccess);
        return;
      }

      const status = await check(permission);

      if (status === RESULTS.GRANTED || status === RESULTS.LIMITED) {
        launch(source, type, onSuccess);
        return;
      }

      if (status === RESULTS.DENIED) {
        const result = await request(permission);

        if (result === RESULTS.GRANTED || result === RESULTS.LIMITED) {
          launch(source, type, onSuccess);
          return;
        }

        if (result === RESULTS.BLOCKED) {
          showSettingsAlert(source, type, onSuccess);
          return;
        }

        if (result === RESULTS.DENIED) {
          const title = 'Permission Required';
          const message = `Please allow ${
            source === 'camera' ? 'camera' : 'photo library'
          } access to upload photos.`;

          Alert.alert(title, message);
          announce(`${title}. ${message}`);
          return;
        }

        if (result === RESULTS.UNAVAILABLE) {
          const title = 'Unavailable';
          const message =
            source === 'camera'
              ? 'Camera is not available on this device or simulator.'
              : 'Photo Library access is not available in this app configuration.';

          Alert.alert(title, message);
          announce(`${title}. ${message}`);
          return;
        }
      }

      if (status === RESULTS.BLOCKED) {
        showSettingsAlert(source, type, onSuccess);
        return;
      }

      if (status === RESULTS.UNAVAILABLE) {
        const title = 'Unavailable';
        const message =
          source === 'camera'
            ? 'Camera is not available on this device or simulator.'
            : 'Photo Library access is not available in this app configuration.';

        Alert.alert(title, message);
        announce(`${title}. ${message}`);
        return;
      }

      const title = 'Permission Required';
      const message = `Please allow ${
        source === 'camera' ? 'camera' : 'photo library'
      } access to upload photos.`;

      Alert.alert(title, message);
      announce(`${title}. ${message}`);
    },
    [launch, showSettingsAlert],
  );

  const handleReturnFromSettings = useCallback(async () => {
    if (!pendingRequest.current) {
      return;
    }

    const {source, type, onSuccess} = pendingRequest.current;
    const permission = getPermissionType(source);

    if (!permission) {
      pendingRequest.current = null;
      launch(source, type, onSuccess);
      return;
    }

    const status = await check(permission);

    if (status === RESULTS.GRANTED || status === RESULTS.LIMITED) {
      pendingRequest.current = null;
      launch(source, type, onSuccess);
    }
  }, [launch]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }

    const subscription = AppState.addEventListener(
      'change',
      (nextAppState: AppStateStatus) => {
        if (
          appState.current.match(/inactive|background/) &&
          nextAppState === 'active'
        ) {
          setTimeout(() => {
            handleReturnFromSettings();
          }, 800);
        }

        appState.current = nextAppState;
      },
    );

    return () => subscription.remove();
  }, [handleReturnFromSettings]);

  const openPicker = useCallback(
    (type: PickerType, onSuccess: (image: PickedImage) => void) => {
      const spec = IMAGE_SPECS[type];

      if (Platform.OS === 'web') {
        launchWebPicker(type, onSuccess);
        return;
      }

      Alert.alert(spec.label, spec.hint, [
        {
          text: 'Camera',
          onPress: () => {
            preLaunchCheck('camera', type, onSuccess);
          },
        },
        {
          text: 'Photo Library',
          onPress: () => {
            preLaunchCheck('library', type, onSuccess);
          },
        },
        {
          text: 'Cancel',
          style: 'cancel',
        },
      ]);

      announce(`${spec.label}. ${spec.hint}`);
    },
    [launchWebPicker, preLaunchCheck],
  );

  return {openPicker};
};
